import { ExpandMore } from "@mui/icons-material";
import { Button, Grid, Stack, styled, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DocumentModal } from "components/DocumentModal";
import { FileSources } from "constants/sockets";
import { useAuthUser } from "context/GameAuthContext";
import React from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { useIntl } from "react-intl";
import "react-quill/dist/quill.snow.css";
import { AlertDialog, Feature } from "../../components/AlertDialog";
import { ReactComponent as FileIcon } from "../../components/doc_icon.svg";
import { FileData } from "../../context/GameContext";
import { useWindowTilesContext } from "../../desktopWindow/context/WindowTilesContext";
import { getFileMeta, isAudio } from "../../fileBrowser/helpers/fileBrowserHelpers";
import { Email } from "../emailTypes";
import { formatSentMail, relativeSentMailDate } from "../emailUtils";
import { Avatar } from "@mui/material";

interface Props {
	mail?: Email;
}

const mobileWidth = window.innerWidth;

const EmailContent: React.FC<Props> = ({ mail }) => {
	const { openWindow } = useWindowTilesContext();
	const [isAlertOpen, setAlertOpen] = React.useState<boolean>(false);
	const classes = containerStyles();
	const [protectedFilePayload, setProtectedFilePayload] = React.useState<any>();
	const onClickAttachment = async (fileToOpen: FileData) => {
		if (fileToOpen.protection?.locked) {
			setProtectedFilePayload({
				fileSource: FileSources.EMAIL_ATTACHMENT,
				payload: {
					mailId: mail?.id,
					attachmentId: fileToOpen.id,
				},
			});
		} else if (fileToOpen.puzzleGamePlugin?.pluginEmbedUrl) {
			const windowHeight = window.innerHeight * 0.85,
				windowWidth = window.innerWidth * 0.75;
			openWindow({
				id: fileToOpen.id!,
				componentName: "EmbeddedPuzzle",
				componentProps: { puzzleGamePlugin: fileToOpen.puzzleGamePlugin },
				windowTitle: fileToOpen.name,
				size: { height: windowHeight, width: windowWidth },
				minSize: { height: windowHeight * 0.5, width: windowWidth * 0.5 },
			});
		} else {
			const file: any = fileToOpen ?? fileToOpen[0];
			const isImage = file.fileUrl.includes("jpg") || file.fileUrl.includes("png");
			const isAudioExt: boolean = isAudio(file.fileUrl);

			const { width: fileWidth, height: fileHeight } = await getFileMeta(fileToOpen, isImage);
			const defaultWindowWidth = isAudioExt ? 690 : 400;
			const defaultWindowHeight = isAudioExt ? 260 : 400;
			const windowWidth = Math.max(defaultWindowWidth, Math.min(fileWidth, window.innerWidth - 200));
			const windowHeight = Math.max(defaultWindowHeight, Math.min(fileHeight, window.innerHeight - 200));

			openWindow({
				id: fileToOpen.id!,
				componentName: "FileViewer",
				componentProps: { file: fileToOpen },
				windowTitle: fileToOpen.name,
				size: { height: windowHeight, width: windowWidth },
				minSize: { height: defaultWindowHeight, width: defaultWindowWidth },
			});
		}
	};

	return (
		<>
			<Grid container direction="column" className={classes.masterContainer}>
				{mail ? (
					<div className={classes.subContainer}>
						<Grid item container direction="row" style={{ marginTop: 6, maxWidth: mobileWidth }}>
							{isDesktop && <EmailActionBar mail={mail} setAlertOpen={setAlertOpen} />}
							<MailMetaData mail={mail} />
							{mail.attachments && (
								<MailAttachement attachments={mail.attachments} onClickAttachment={onClickAttachment} />
							)}
						</Grid>
						<EmailBody mail={mail} />
					</div>
				) : (
					<Grid item className={classes.noEmail}>
						<Typography variant="body2">{"No email selected."}</Typography>
					</Grid>
				)}
			</Grid>
			<AlertDialog isOpen={isAlertOpen} feature={Feature.EMAIL} onCancel={() => setAlertOpen(false)} />
			<DocumentModal
				isOpen={!!protectedFilePayload}
				onClose={() => setProtectedFilePayload(undefined)}
				data={protectedFilePayload}
			/>
		</>
	);
};

export default EmailContent;

const EmailActionBar: React.FC<{ mail: Email; setAlertOpen }> = ({ mail, setAlertOpen }) => {
	return (
		<Grid
			id="card-email-subject"
			item
			container
			direction="row"
			justifyContent="space-between"
			alignItems="center"
			sx={{ marginBottom: "16px", flexWrap: "nowrap" }}
		>
			<Grid item sx={{ flexGrow: 1, overflow: "hidden" }}>
				<Typography
					variant="subtitle1"
					style={{ fontWeight: "bold", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
				>
					{mail.mailSubject?.slice(0, 50)}
				</Typography>
			</Grid>
			<Grid item sx={{ flexShrink: 0, textAlign: "right", whiteSpace: "nowrap" }}>
				<Typography variant="body2" color="textSecondary">
					{isDesktop ? relativeSentMailDate(mail.sentDate) : formatSentMail(mail.sentDate)}
				</Typography>
			</Grid>
		</Grid>
	);
};

const MailMetaData: React.FC<{ mail: Email }> = ({ mail }) => {
	const { name, email } = useAuthUser();
	const intl = useIntl();

	const userEmail = { avatarColor: "#000", name, email };

	const receiver = mail?.category === "sent" ? mail?.receiver : userEmail;
	const sender = mail?.category === "sent" ? userEmail : mail?.sender;
	const avatarColor = sender.avatarColor;

	const classes = mailMetaDataStyles({ avatar: avatarColor });

	return (
		<Grid
			id="card-email-metadata"
			item
			container
			direction="row"
			justifyContent="space-between"
			alignItems="flex-start"
			style={{ maxWidth: mobileWidth, margin: "0 0px" }}
		>
			<Grid item container direction="row" alignItems={"center"} xs={7}>
				{isMobile && <Avatar className={classes.avatar}>{sender.name.match(/\b(\w)/g)?.join("")}</Avatar>}

				<Grid sx={{ marginLeft: isMobile ? "8px" : 0 }}>
					<Grid item style={{ wordBreak: "break-all" }}>
						{isDesktop && (
							<Typography id="text-email-from" className={classes.text}>
								<b>
									{intl.formatMessage({
										id: "EMAIL.content.from.text",
									})}
								</b>
								{sender.name} {`(${sender.email})`}
							</Typography>
						)}
						{isMobile && (
							<Typography id="text-email-from" sx={{ fontWeight: "bold" }} className={classes.text}>
								{sender.name}
							</Typography>
						)}
					</Grid>
					<Grid item>
						<Typography id="text-email-to" className={classes.text}>
							<b>
								{intl.formatMessage({
									id: "EMAIL.content.to.text",
								})}
							</b>
							{" " + receiver.name} {isDesktop && `(${receiver.email})`}
						</Typography>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

type MailAttachementProps = {
	onClickAttachment: (fileData) => void;
	attachments: FileData[];
};

const MailAttachement: React.FC<MailAttachementProps> = ({ attachments, onClickAttachment }) => {
	const classes = mailAttachementStyle();
	return (
		<Stack flexDirection="row" className={classes.container}>
			{attachments.map((file) => {
				const isImage: boolean | undefined = file?.fileUrl?.includes("jpg") || file?.fileUrl?.includes("png");
				return (
					<Button
						id="section-attachment"
						key={file.name}
						variant="outlined"
						onClick={() => onClickAttachment(file)}
						className={classes.button}
						endIcon={<ExpandMore />}
						title={file.name}
					>
						{isImage ? (
							<ImageFileThumbnail src={file!.fileUrl!} />
						) : (
							<FileIcon style={{ maxHeight: 25, maxWidth: 25 }} />
						)}
						<span className="filename">{file.name}</span>
					</Button>
				);
			})}
		</Stack>
	);
};

const EmailBody: React.FC<{ mail: Email }> = ({ mail }) => {
	const classes = emailBodyStyles();
	return (
		<Grid id="card-email-body" item container direction="row" className={classes.container}>
			<Grid item>
				<EmailText className="ql-editor" dangerouslySetInnerHTML={{ __html: mail.mailContent }} />
			</Grid>
		</Grid>
	);
};

const EmailText = styled("div")(({ theme }) => ({
	padding: "0 !important",
	height: "fit-content !important",
	[theme.breakpoints.down("mobile")]: {
		marginTop: 30,
		"& *": {
			width: mobileWidth - 20, //20 is offset need to fit in the screen
			height: "fit-content",
		},
	},
}));

const ImageFileThumbnail = (props: { src: string }) => {
	return <img style={{ width: "100%", maxHeight: 25, maxWidth: 25 }} src={props.src} alt={props.src} />;
};

const containerStyles = makeStyles((theme) => ({
	masterContainer: {
		padding: "10px 20px",
		overflowY: "auto",
		maxWidth: "100%",
		height: "100%",
		backgroundColor: "#fff",
		overflow: "hidden",
		[theme.breakpoints.down("mobile")]: {
			padding: 0,
			maxWidth: mobileWidth,
		},
	},
	subContainer: {
		maxWidth: mobileWidth,
		padding: "10px 20px",
	},
	noEmail: {
		textAlign: "center",
	},
}));

const mailMetaDataStyles = makeStyles((theme) => ({
	avatarContainer: {
		[theme.breakpoints.down("mobile")]: {
			alignSelf: "flex-start",
		},
	},
	avatar: {
		backgroundColor: (props: any) => props.avatar,
		[theme.breakpoints.down("mobile")]: {
			width: 30,
			height: 30,
		},
	},
	dateContainer: {
		[theme.breakpoints.down("mobile")]: {
			flexBasis: 0,
			alignSelf: "flex-start",
			justifySelf: "flex-end",
		},
	},
	text: {
		fontSize: "0.875rem",
		textOverflow: "ellipsis",
		overflow: "hidden",
		whiteSpace: "nowrap",
	},
}));

const mailAttachementStyle = makeStyles((theme) => ({
	container: {
		alignItems: "center",
		justify: "flex-start",
		marginTop: 8,
		maxWidth: "300px",
		width: "100%",
	},
	button: {
		textTransform: "none",
		backgroundColor: "#FAFAFA",
		borderColor: "#FAFAFA",
		"& .filename": {
			// ellipsis
			overflow: "hidden",
			textOverflow: "ellipsis",
			marginLeft: 10,
			whiteSpace: "nowrap",
		},
	},
}));

const emailBodyStyles = makeStyles((theme) => ({
	container: {
		padding: "20px 20px 20px 0px",
		// maxWidth: mobileWidth,
		width: "100%",
		fontSize: "0.875rem",
		overflow: "hidden",
		[theme.breakpoints.down("mobile")]: {
			overflowY: "scroll",
			padding: 0,
		},
	},
}));
